import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyBurnInfoModal from 'loyalty/components/LoyaltyBurnInfoModal'
import LoyaltyBurnSliderBase from 'loyalty/components/LoyaltyBurnSliderBase'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import { CLEAR_SEARCH_BURN_BATCH_CALCULATIONS } from 'actions/actionConstants'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import getOfferListKey from 'lib/offer/offerListKey'
import useSwitchRegion from 'hooks/useSwitchRegion'
import config from 'constants/config'

function LoyaltyBurnSliderSearch() {
  const showModal = useContext(ModalContext)
  const [tier] = useCurrentLoyaltyTier()
  const { hasSelectedBurnToggle, setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()
  const dispatch = useAppDispatch()

  const userPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const filters = useAppSelector(getUrlOfferListFilters)
  const offerListKey = getOfferListKey(filters)
  const batchCalculations = useAppSelector(state => state.loyalty.pointsBurnBatchCalculations ?? {})
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const { isAnyVariant: isBurnEnabled } = useExperiment(Experiments.loyaltySocieteBurn)

  const loyaltyCurrencyLabel = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyAccountRegion)?.currencyCode
  }, [loyaltyAccountRegion])

  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)

  useEffect(() => {
    dispatch({ type: CLEAR_SEARCH_BURN_BATCH_CALCULATIONS })
  }, [offerListKey, dispatch])

  const maxBurnPoints = useMemo(() => {
    const maxPriceInPoints = Object.values(batchCalculations)
      .reduce((max, result) => Math.max(max, result?.data?.priceInPoints ?? 0), 0)
    return maxPriceInPoints > 0 ? Math.min(userPoints, maxPriceInPoints) : userPoints
  }, [batchCalculations, userPoints])

  const isLoadingMax = hasSelectedBurnToggle && Object.keys(batchCalculations).length === 0

  const [localValue, setLocalValue] = useState<number>(() => userPoints)

  useEffect(() => {
    if (!hasSelectedBurnToggle) return
    setLocalValue(prev => {
      const capped = Math.min(prev, maxBurnPoints)
      if (capped !== prev) setPointsToBurn(capped)
      return capped
    })
  }, [hasSelectedBurnToggle, maxBurnPoints, setPointsToBurn])

  const handleSliderDispatch = useCallback((value: number) => {
    setPointsToBurn(value)
  }, [setPointsToBurn])

  const debouncedSliderDispatch = useDebouncedCallback(handleSliderDispatch, 100, [handleSliderDispatch])

  const onSliderChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, value: number) => {
    setLocalValue(value)
    debouncedSliderDispatch(value)
  }, [debouncedSliderDispatch])

  const handleToggle = useCallback(async() => {
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    if (hasSelectedBurnToggle) {
      setLocalValue(maxBurnPoints)
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
    } else {
      setLocalValue(maxBurnPoints)
      setSearchBurnEnabled(true)
      setPointsToBurn(maxBurnPoints)
    }
  }, [isLoggedIn, hasSelectedBurnToggle, maxBurnPoints, setSearchBurnEnabled, setPointsToBurn, showModal])

  const handleReset = useCallback(() => {
    setLocalValue(MINIMUM_POINTS_TO_APPLY)
    setPointsToBurn(MINIMUM_POINTS_TO_APPLY)
  }, [setPointsToBurn])

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyBurnInfoModal />)
  }, [showModal])

  const shouldShow = useMemo(() => {
    if (!isBurnEnabled) return false
    if (!isLoyaltyEnabled) return false
    if (!isLoggedIn) return true
    if (!hasLoyaltyAccount) return false
    if (!isInLoyaltyRegion) return true
    return userPoints >= MINIMUM_POINTS_TO_APPLY
  }, [isBurnEnabled, isLoyaltyEnabled, isLoggedIn, hasLoyaltyAccount, isInLoyaltyRegion, userPoints])

  if (!shouldShow) {
    return null
  }

  return (
    <LoyaltyBurnSliderBase
      isEnabled={hasSelectedBurnToggle}
      loading={isLoadingMax}
      onToggle={handleToggle}
      tier={tier}
      hasLoyaltyAccount={hasLoyaltyAccount}
      userPoints={userPoints}
      maxBurnPoints={maxBurnPoints}
      minBurnPoints={MINIMUM_POINTS_TO_APPLY}
      localValue={localValue}
      onSliderChange={onSliderChange}
      onReset={handleReset}
      onInfoClick={handleInfoClick}
      wrongRegion={isLoggedIn && !isInLoyaltyRegion}
      switchRegionPath={switchRegionPath}
      onSwitchRegion={handleSwitchRegion}
      loyaltyCurrencyLabel={loyaltyCurrencyLabel}
    />
  )
}

export default LoyaltyBurnSliderSearch
